import { useEffect, useRef } from 'react';
import { registerBackgroundMusic } from '../utils/audioChannel';

/**
 * Custom hook to manage background music with dynamic volume control
 * @param {boolean} showSplash - Whether splash screen is showing
 * @param {boolean} showIntro - Whether intro sequence is showing
 * @param {boolean} isMusicMuted - Whether music is muted
 * @param {Object} animations - Object containing all active animations
 * @returns {Object} - Music control functions and refs
 */
export function useBackgroundMusic(showSplash, showIntro, isMusicMuted, animations) {
  const bgMusicRef = useRef(null);
  const soundtrackPlaylist = useRef([]);
  const currentTrackIndex = useRef(0);

  // Initialize background music
  // 🐛 BUG FIX: Removed `isMusicMuted` from deps. Previously, every mute
  // toggle tore down the bgMusicRef and re-created the Audio element with
  // a freshly-shuffled playlist. On iOS Safari that new .play() lands
  // outside the original user-gesture window and silently fails, leaving
  // the soundtrack permanently silent for the rest of the session. The
  // mute/unmute behavior is handled in the volume-control effect below.
  useEffect(() => {
    if (!showSplash && !showIntro && !bgMusicRef.current) {
      const allSoundtracks = [
        '/assets/audio/soundtrack-1.mp3',
        '/assets/audio/soundtrack-2.mp3',
        '/assets/audio/soundtrack-3.mp3',
        '/assets/audio/soundtrack-nk-propaganda.mp3',
        '/assets/audio/american-1.mp3',
        '/assets/audio/american-2.mp3',
        '/assets/audio/europe-1.mp3',
        '/assets/audio/europe-2.mp3',
        '/assets/audio/asian-1.mp3',
        '/assets/audio/asian-2.mp3',
        '/assets/audio/diplomatic-1.mp3',
        '/assets/audio/diplomatic-2.mp3',
        '/assets/audio/defeat-1.mp3',
        '/assets/audio/defeat-2.mp3',
        '/assets/audio/victory-1.mp3',
        '/assets/audio/victory-2.mp3',
        '/assets/audio/suspense-1.mp3',
        '/assets/audio/suspense-2.mp3',
        '/assets/audio/ymca-title-song.mp3',  // YMCA-inspired title song
        '/assets/audio/russian-ballet-1.mp3',  // Russian Ballet 1
        '/assets/audio/russian-ballet-2.mp3',  // Russian Ballet 2
        '/assets/audio/swan-lake.mp3',         // Swan Lake
        '/assets/audio/imperial-march.mp3',    // Imperial March Clone
        '/assets/audio/starship-hangar.mp3'    // ✨ NEW: Star Wars Majestic Starship Hangar
      ];
      
      // Shuffle function (Fisher-Yates algorithm)
      const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };
      
      soundtrackPlaylist.current = shuffleArray(allSoundtracks);
      currentTrackIndex.current = 0;
      
      const firstTrack = soundtrackPlaylist.current[0];
      const bgMusic = new Audio(firstTrack);
      bgMusic.volume = 0.02; // 🎚️ Base music volume — barely audible so SFX dominate
      bgMusicRef.current = bgMusic;
      registerBackgroundMusic(bgMusic);
      
      console.log(`🎵 Shuffled playlist created: ${soundtrackPlaylist.current.map((t, i) => `${i+1}. ${t.split('/').pop()}`).join(', ')}`);
      console.log(`🎵 Now playing track 1/${allSoundtracks.length}: ${firstTrack.split('/').pop()}`);
      
      const playNextTrack = () => {
        currentTrackIndex.current++;
        
        if (currentTrackIndex.current >= soundtrackPlaylist.current.length) {
          console.log(`🔄 Playlist complete! Reshuffling...`);
          soundtrackPlaylist.current = shuffleArray(allSoundtracks);
          currentTrackIndex.current = 0;
          console.log(`🎵 New shuffled playlist: ${soundtrackPlaylist.current.map((t, i) => `${i+1}. ${t.split('/').pop()}`).join(', ')}`);
        }
        
        const nextTrack = soundtrackPlaylist.current[currentTrackIndex.current];
        const nextMusic = new Audio(nextTrack);
        nextMusic.volume = 0.02; // 🎚️ Base music volume — barely audible so SFX dominate
        bgMusicRef.current = nextMusic;
        registerBackgroundMusic(nextMusic);
        
        console.log(`🎵 Now playing track ${currentTrackIndex.current + 1}/${allSoundtracks.length}: ${nextTrack.split('/').pop()}`);
        
        nextMusic.addEventListener('ended', playNextTrack);
        
        // Always try to play next track; the mute-control effect will pause
        // it on the next tick if the user has it muted.
        nextMusic.play().catch(e => console.log('Next track autoplay failed:', e));
      };
      
      bgMusic.addEventListener('ended', playNextTrack);
      
      if (!isMusicMuted) {
        bgMusic.play().catch(e => console.log('BG music autoplay blocked:', e));
      }
    }
    
    return () => {
      if (bgMusicRef.current) {
        bgMusicRef.current.pause();
        bgMusicRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSplash, showIntro]);
  
  // Dynamic volume & playback control
  // 📱 iOS Safari only allows ONE HTML5 audio channel at a time. While
  // background music is playing, sound effects like the air-strike siren,
  // bomb blasts, and nuke explosions silently fail to play. To fix this,
  // we explicitly PAUSE the music (not just lower volume) during any combat
  // or cinematic sequence, then resume it afterward. This frees the audio
  // channel and lets the dramatic SFX actually be heard on phones.
  useEffect(() => {
    if (!bgMusicRef.current) return;
    
    if (isMusicMuted) {
      bgMusicRef.current.pause();
      return;
    }
    
    // Any of these means a "scene" is playing its own audio — get out of the way
    const sceneActive = !!(
      animations.propagandaAnimation ||
      animations.diplomacyAnimation ||
      animations.blackmailAnimation ||
      animations.deployTroopsAnimation ||
      animations.nuclearStrikeAnimation ||
      animations.airStrikeAnimation ||
      animations.nukeReadyCelebration
    );
    
    if (sceneActive) {
      // PAUSE entirely so iOS hands the audio channel to the SFX
      if (!bgMusicRef.current.paused) {
        bgMusicRef.current.pause();
        console.log('🔇 Music paused — scene audio has the floor');
      }
    } else {
      // Resume normal background ambiance at barely-audible volume
      bgMusicRef.current.volume = 0.02;
      if (bgMusicRef.current.paused) {
        bgMusicRef.current.play().catch(e => console.log('Resume music failed:', e));
      }
    }
  }, [
    animations.propagandaAnimation,
    animations.diplomacyAnimation,
    animations.blackmailAnimation,
    animations.deployTroopsAnimation,
    animations.nuclearStrikeAnimation,
    animations.airStrikeAnimation,
    animations.nukeReadyCelebration,
    isMusicMuted
  ]);

  return { bgMusicRef };
}
